// src/flow/RegenerateImageModal.jsx
// Two questions, one at a time: the type of image, then the colour theme.
// Picking a colour confirms and generates.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../components/common/Button";
import useThemes from "../utils/useThemes";
import { fromApiColors } from "../utils/brandApi";
import arrowLeft from "../assets/common/arrow-left.svg";

const FORMATS = ["square", "infographic", "rectangle"];

const cardClass = (selected, unavailable) =>
  `flex transition-all focus:outline-none focus:ring-2 focus:ring-primary-400
   text-black-800 text-lg font-bold rounded-lg border-2 items-center text-left
   w-full p-3 px-4 my-2 gap-3
   ${unavailable
     ? "bg-black-0 border-black-40 opacity-50 cursor-not-allowed"
     : selected
       ? "bg-primary-200 border-primary-300"
       : "bg-black-0 border-black-40 hover:border-primary-300"}`;

export default function RegenerateImageModal({
  open,
  onClose,
  userEmail,
  canCreateInfographic,
  onConfirm,
  onOpenAccount,
}) {
  const { t } = useTranslation();
  const { themes } = useThemes(userEmail);
  const [step, setStep] = useState("format");   // "format" | "colors"
  const [format, setFormat] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("format");
      setFormat(null);
    }
  }, [open]);

  if (!open) return null;

  const pickColors = (themeId) => {
    const theme = themes.find((th) => String(th.theme_id) === String(themeId));
    const colors = themeId === "auto" || !theme ? null : fromApiColors(theme);
    onConfirm(format, colors);
  };

  return (
    <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-4 w-full max-w-[420px] max-h-[90vh] overflow-y-auto shadow-xl">
        {step === "colors" && (
          <div className="pb-4 flex">
            <Button
              variant="ghost"
              onClick={() => {
                setFormat(null);
                setStep("format");
              }}
              className="w-auto u-btn-sm"
            >
              <img src={arrowLeft} alt="" /> {t("buttons.back")}
            </Button>
          </div>
        )}

        {step === "format" ? (
          <div>
            <p className="text-xl font-bold text-black-800 mb-4">
              {t("sources.imageFormat.title")}
            </p>
            {FORMATS.map((id) => {
              const unavailable = id === "infographic" && !canCreateInfographic;
              return (
                <button
                  key={id}
                  type="button"
                  disabled={unavailable}
                  onClick={() => {
                    setFormat(id);
                    setStep("colors");
                  }}
                  className={cardClass(format === id, unavailable)}
                >
                  <span className="text-base lg:text-lg font-bold text-black-800">
                    {t(`sources.imageFormat.${id}`)}
                  </span>
                </button>
              );
            })}
            {!canCreateInfographic && (
              <div className="space-y-2 mb-2">
                <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
                  {t("sources.unavailable.infographic")}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-xl font-bold text-black-800 mb-4">
              {t("sources.brandColors.title")}
            </p>
            {themes.map((theme) => (
              <button
                key={theme.theme_id}
                type="button"
                onClick={() => pickColors(theme.theme_id)}
                className={cardClass(false, false)}
              >
                <span className="text-base lg:text-lg font-bold text-black-800 flex-1">
                  {theme.name}
                </span>
                <span className="flex gap-1 flex-shrink-0">
                  {[theme.background, theme.text, theme.highlight].map((c, i) => (
                    <span
                      key={i}
                      className="w-3 h-3 rounded-full border border-black-40"
                      style={{ background: c }}
                    />
                  ))}
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => pickColors("auto")}
              className={cardClass(false, false)}
            >
              <span className="text-base lg:text-lg font-bold text-black-800">
                {t("sources.brandColors.useAuto")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onOpenAccount?.()}
              className="text-base text-black-800 font-bold underline hover:text-black mb-2 ml-1"
            >
              {t("sources.brandColors.setupColors")} →
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 text-sm sm:text-lg font-bold">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            {t("buttons.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
}